import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, ExternalLink, Info, Loader2, RefreshCw } from 'lucide-react';

/**
 * Ana sayfadan hangi listeleme (kategori) sayfasına gidiliyor — CANLI.
 *
 * Backend: GET /api/site-flow/home-categories (GA4, marka başına).
 * Marka `localStorage.currentBrandId` üzerinden geliyor; sayfa markadan bağımsız
 * yazıldı, rota App.js'te allowBrandIds ile kısıtlı.
 *
 * 🔴🔴 Sayfanın çekirdek fikri: **ölçüm var mı yok mu, sayfa bunu kendisi söyler.**
 * Bir sitede `page_view` olayı yoksa listeleme sayfası GA4'te "sayfa" olarak var
 * olmaz; görüntülenme 0 döner. Bu SIFIR DEĞİL, ÖLÇÜLMEDİ demektir. Üstteki
 * ölçüm şeridi ve tablodaki "ölçülmüyor" rozeti bunun için var — kaldırılırsa
 * sayfa "kategoriye kimse gitmiyor" diye okunur ve yanlış karar aldırır.
 *
 * Ölçüm yokken elde kalan iki gerçek sinyal gösteriliyor, ikisi de etiketli:
 *   • Ürüne gönderdiği tık — listeleme sayfasından ürüne yapılan geçiş.
 *   • Ana sayfadan kategori atfı — `pageReferrer` × `itemCategory`. ATIFTIR.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const nf = (n) => Math.round(Number(n) || 0).toLocaleString('tr-TR');
const pct1 = (a, b) => (b > 0 ? `%${((a / b) * 100).toFixed(1).replace('.', ',')}` : '—');
const dTR = (s) => (s ? s.split('-').reverse().join('.') : '—');

const TUR_ADI = {
  urun: 'Ürün sayfası', listeleme: 'Listeleme sayfası', arama: 'Site içi arama',
  huni: 'Sepet / üyelik / ödeme', anasayfa: 'Ana sayfa',
};

function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="mb-4">
      <h2 className="text-[12px] font-bold uppercase tracking-wider text-indigo-700">{eyebrow}</h2>
      <p className="mt-1.5 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
      {children ? (
        <p className="mt-1.5 max-w-[85ch] text-[13.5px] leading-relaxed text-slate-500">{children}</p>
      ) : null}
    </div>
  );
}

function Bar({ value, max, tone = 'indigo' }) {
  const w = max > 0 ? Math.max(1.5, (value / max) * 100) : 0;
  const bg = tone === 'slate' ? 'bg-slate-400' : 'bg-indigo-500';
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${bg}`} style={{ width: `${w}%` }} />
    </div>
  );
}

/* ── Ölçüm şeridi ─────────────────────────────────────────────────────────── */
function OlcumSeridi({ olcum, host, propertyId }) {
  const ok = olcum?.pageView;
  const t = olcum?.teshis;
  const yanlisProperty = t?.sonuc === 'yanlis-property';
  const olaylar = Object.entries(olcum?.olaylar || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div
      className={`rounded-xl border p-4 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
    >
      <div className="flex items-start gap-3">
        {ok
          ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-700" />
          : <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700" />}
        <div className="min-w-0">
          <p className={`text-[13.5px] font-bold ${ok ? 'text-emerald-900' : 'text-amber-900'}`}>
            {ok
              ? 'Listeleme sayfası görüntülenmesi ölçülüyor.'
              : yanlisProperty
                ? 'Sayfa görüntülenmeleri ölçülüyor — ama BAŞKA bir GA4 property’sinde. Glowytics yanlış property’ye bağlı.'
                : `${host} sitesine page_view etiketi eklenmemiş — listeleme sayfası görüntülenmesi ÖLÇÜLMÜYOR.`}
          </p>
          <p className={`mt-1 max-w-[85ch] text-[13px] leading-relaxed ${ok ? 'text-emerald-800' : 'text-amber-800'}`}>
            {ok ? (
              <>Ana sayfa → listeleme sayfası geçişi doğrudan sayılıyor; aşağıdaki
                “Görüntülenme” ve “Ana sayfadan giriş” sütunları gerçek ölçümdür.</>
            ) : yanlisProperty ? (
              <>
                {host} sayfada <b>{(t.baglanmayan || []).join(', ')}</b> ölçüm kimliğine de veri gönderiyor;
                Glowytics’e bağlı property <b>{propertyId}</b> ise yalnızca{' '}
                <b>{(t.propertyOlcumKimlikleri || []).map((x) => x.id).join(', ')}</b> topluyor.
                Sayfa görüntülenmeleri ve listeleme görüntülemeleri <b>bağlı olmayan property’de duruyor</b> —
                yani veri var, biz göremiyoruz. O property’ye <b>Görüntüleyici (Viewer) yetkisi</b> alınıp
                Glowytics’e bağlanırsa bu sayfa hiçbir değişiklik gerekmeden gerçek görüntülenme
                rakamlarını gösterir; <b>geçmiş veri de gelir</b>.
              </>
            ) : (
              <>Kategori sayfaları hiçbir olay göndermediği için GA4’te <em>sayfa</em> olarak var olmuyorlar:
                görüntülenmeleri 0 görünür ve ana sayfadan oraya yapılan tık hiçbir yere kaydedilmez.
                Aşağıdaki <b>0</b> değerleri “kimse gitmedi” değil <b>“ölçülmedi”</b> demektir.
                Düzeltmesi GTM’de tek bir <code className="rounded bg-white/70 px-1">page_view</code> etiketi;
                geriye dönük veri getirmez, eklendiği günden itibaren birikir.</>
            )}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {olaylar.map(([ad, n]) => (
              <span
                key={ad}
                className="rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
              >
                {ad} <b className="text-slate-900">{nf(n)}</b>
              </span>
            ))}
            {!ok ? (
              <span className="rounded border border-amber-300 bg-white px-1.5 py-0.5 font-mono text-[11px] text-amber-800">
                page_view <b>yok</b>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Huni ─────────────────────────────────────────────────────────────────── */
/* page_view GEREKTİRMEZ: adımların hepsi e-ticaret olayı. Bu yüzden ölçüm
   eksikliğinden etkilenmiyor ve rakamların tamamı gerçek. */
function Huni({ adimlar, not }) {
  const ilk = adimlar[0]?.v || 0;
  // En dar boğaz: adımlar arası en düşük geçiş oranı. Sadece daralan adımlara bak
  // (ürün görüntüleme oturumdan büyük olabiliyor, o bir düşüş değil).
  const gecisler = adimlar.slice(1).map((x, k) => x.v / (adimlar[k].v || 1));
  const enDusuk = gecisler.length ? Math.min(...gecisler.filter((g) => g <= 1)) : null;
  return (
    <div className="flex flex-col gap-2">
      {adimlar.map((a, i) => {
        const g = i ? gecisler[i - 1] : null;
        const dar = g !== null && g <= 1 && g === enDusuk;
        return (
          <div key={a.ad} className="flex items-center gap-2.5">
            <div className="w-[8.5rem] shrink-0 text-[12.5px] text-slate-600">{a.ad}</div>
            {/* Sayı çubuğun DIŞINDA: dar çubukta içeride kalınca kırpılıyordu. */}
            <div className="w-[4.5rem] shrink-0 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-900">
              {nf(a.v)}
            </div>
            <div className="h-5 flex-1 overflow-hidden rounded bg-slate-100">
              <div
                className={`h-full rounded ${dar ? 'bg-rose-500' : 'bg-indigo-500'}`}
                style={{ width: `${ilk > 0 ? Math.max(1.5, (a.v / ilk) * 100) : 0}%` }}
              />
            </div>
            <div className="w-[4.5rem] shrink-0 text-right font-mono text-[12px] tabular-nums">
              {g === null ? <span className="text-slate-300">—</span>
                : g > 1 ? <span className="text-slate-500">×{g.toFixed(1).replace('.', ',')}</span>
                  : <span className={dar ? 'font-semibold text-rose-600' : 'text-slate-500'}>
                      %{(g * 100).toFixed(1).replace('.', ',')}
                    </span>}
            </div>
          </div>
        );
      })}
      {not ? <p className="mt-1 text-[12px] leading-relaxed text-slate-500">{not}</p> : null}
    </div>
  );
}

/* ── Kategori ağacı (atıf) ────────────────────────────────────────────────── */
function AgacSatiri({ node, toplam, max, derinlik }) {
  const [acik, setAcik] = useState(true);   // liste tam açık gelsin
  const cocuk = node.cocuk || [];
  return (
    <>
      <tr className="border-b border-slate-100 last:border-0">
        <td className="py-1.5 pr-3" style={{ paddingLeft: `${derinlik * 18}px` }}>
          {cocuk.length ? (
            <button
              type="button"
              onClick={() => setAcik((v) => !v)}
              className="mr-1.5 w-4 text-[11px] text-slate-400 hover:text-slate-700"
            >
              {acik ? '−' : '+'}
            </button>
          ) : <span className="mr-1.5 inline-block w-4" />}
          <span className={derinlik === 0 ? 'text-[13.5px] font-semibold text-slate-900' : 'text-[13px] text-slate-700'}>
            {node.ad}
          </span>
        </td>
        <td className="w-[26%] py-1.5 pr-3"><Bar value={node.goruntuleme} max={max} tone={derinlik ? 'slate' : 'indigo'} /></td>
        <td className="py-1.5 pr-3 text-right font-mono text-[12.5px] tabular-nums text-slate-800">{nf(node.goruntuleme)}</td>
        <td className="py-1.5 pr-4 text-right font-mono text-[12.5px] tabular-nums text-slate-500">{pct1(node.goruntuleme, toplam)}</td>
        <td className="py-1.5 pr-3 text-right font-mono text-[12.5px] tabular-nums text-slate-600">{nf(node.sGoruntuleme)}</td>
        <td className="py-1.5 pr-3 text-right font-mono text-[12.5px] tabular-nums text-slate-600">{nf(node.sAtc)}</td>
        <td className="py-1.5 pr-3 text-right font-mono text-[12.5px] tabular-nums text-slate-600">{nf(node.sSatis)}</td>
        <td className="py-1.5 text-right font-mono text-[12.5px] tabular-nums text-slate-800">{nf(node.sCiro)} ₺</td>
      </tr>
      {acik ? cocuk.map((c) => (
        <AgacSatiri key={c.key} node={c} toplam={toplam} max={max} derinlik={derinlik + 1} />
      )) : null}
    </>
  );
}

/* ── Sayfa ────────────────────────────────────────────────────────────────── */
export default function AnasayfaKategoriAkisi() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [hepsi, setHepsi] = useState(false);
  const [bosKategori, setBosKategori] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/site-flow/home-categories?days=${days}${refresh ? '&refresh=1' : ''}`,
        { credentials: 'include', headers }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Ana sayfa akışı yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days]);

  useEffect(() => { load(); }, [load]);

  // Düz kategori listesini ağaca çevir — backend `ust` alanıyla bağı veriyor.
  const agac = useMemo(() => {
    const rows = data?.kategoriAgaci || [];
    const map = new Map(rows.map((r) => [r.key, { ...r, cocuk: [] }]));
    const kok = [];
    for (const r of map.values()) {
      const ust = r.ust ? map.get(r.ust) : null;
      if (ust) ust.cocuk.push(r); else kok.push(r);
    }
    const sirala = (a) => { a.sort((x, y) => y.goruntuleme - x.goruntuleme); a.forEach((n) => sirala(n.cocuk)); };
    sirala(kok);
    if (bosKategori) return kok;
    /* Ana sayfadan hiç trafik almayan dalları ele. Taksonomide aynı kategorinin
       İngilizce kopyaları da var (Toy Cars ↔ Oyuncak Arabalar); hepsi 0 ile
       geliyor ve listeyi okunmaz hale getiriyordu. */
    const buda = (a) => a
      .filter((n) => n.goruntuleme > 0)
      .map((n) => ({ ...n, cocuk: buda(n.cocuk) }));
    return buda(kok);
  }, [data, bosKategori]);

  const olcumVar = !!data?.meta?.olcum?.pageView;
  const liste = data?.listeleme || [];
  const gosterilen = hepsi ? liste : liste.slice(0, 25);
  const maxTik = liste.length ? liste[0].urunTiki : 0;
  const maxGorunum = liste.reduce((m, x) => Math.max(m, x.gorunum), 0);
  const kaynak = data?.kaynak;
  const anaSayfa = data?.anaSayfa;

  if (!brandId) {
    return <div className="p-6 text-[14px] text-slate-500">Önce bir marka seçin.</div>;
  }

  return (
    <div className="mx-auto max-w-[1180px] px-4 pb-16 pt-5">
      {/* başlık */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Ana sayfadan hangi kategoriye gidiliyor
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">
            {data ? (
              <>
                {data.meta.brandName} · {data.meta.host} · GA4 {data.meta.propertyId} ·{' '}
                {dTR(data.meta.start)} – {dTR(data.meta.end)}
                {data.meta.cached ? ' · önbellekten' : ''}
              </>
            ) : 'GA4’ten canlı çekiliyor…'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-[12.5px] font-medium ${
                  days === d ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {d} gün
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Tazele
          </button>
        </div>
      </div>

      {!data && loading ? (
        <div className="flex h-64 items-center justify-center text-slate-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : null}

      {data ? (
        <div className="flex flex-col gap-10">
          <OlcumSeridi olcum={data.meta.olcum} host={data.meta.host} propertyId={data.meta.propertyId} />

          {/* 01 — ana sayfadan kategori listesi (sayfanın ASIL cevabı, en üstte) */}
          <section>
            <SectionHead
              eyebrow="01 — Ana sayfadan kategoriye"
              title="Ana sayfadan çıkanlar hangi kategorilere gidiyor"
            >
              Ana sayfadan çıkıp bir ürüne giden {nf(anaSayfa.urunGoruntulemesi)} görüntülemenin kategori ağacı.
              Ana sayfadan trafik almayan kategoriler listeden çıkarıldı.
              Kategori bilgisi ürünün kendi <code className="rounded bg-slate-100 px-1">itemCategory</code>’sinden geliyor.
              <b className="text-slate-700"> Bu bir atıftır</b>: listeleme sayfasının görüntülendiğini değil,
              ana sayfadan çıkanın hangi rafa indiğini gösterir.
            </SectionHead>
            <div className="mb-2 flex justify-end">
              <label className="flex cursor-pointer items-center gap-1.5 text-[12.5px] text-slate-500">
                <input
                  type="checkbox"
                  checked={bosKategori}
                  onChange={(e) => setBosKategori(e.target.checked)}
                  className="h-3.5 w-3.5 accent-indigo-600"
                />
                Ana sayfadan trafik almayanları da göster
              </label>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-3">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 text-left font-mono text-[10.5px] uppercase tracking-wider text-slate-400">
                    <th className="pb-1.5 font-medium">Kategori</th>
                    <th className="pb-1.5" />
                    <th className="pb-1.5 text-right font-medium">Ana sayfadan</th>
                    <th className="pb-1.5 pr-4 text-right font-medium">Pay</th>
                    <th className="pb-1.5 text-right font-medium">Site: görüntüleme</th>
                    <th className="pb-1.5 text-right font-medium">Sepete ekleme</th>
                    <th className="pb-1.5 text-right font-medium">Satış</th>
                    <th className="pb-1.5 text-right font-medium">Ciro</th>
                  </tr>
                </thead>
                <tbody>
                  {agac.map((n) => (
                    <AgacSatiri
                      key={n.key}
                      node={n}
                      toplam={anaSayfa.urunGoruntulemesi}
                      max={agac[0]?.goruntuleme || 1}
                      derinlik={0}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 02 — huni */}
          {data.huni ? (
            <section>
              <SectionHead eyebrow="02 — Huni" title="Oturumdan satışa">
                Bu bölümün tamamı gerçek ölçüm — adımların hepsi e-ticaret olayı, page_view’e
                ihtiyaç duymuyor. Sağdaki yüzde bir önceki adımdan geçenlerin oranı (× işareti artışı gösterir);
                <b className="text-rose-600"> kırmızı çubuk en dar boğaz</b>.
              </SectionHead>
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="mb-3 text-[13px] font-semibold text-slate-900">Site geneli</p>
                  <Huni
                    adimlar={[
                      { ad: 'Oturum', v: data.huni.site.oturum },
                      { ad: 'Ürün görüntüleme', v: data.huni.site.goruntuleme },
                      { ad: 'Sepete ekleme', v: data.huni.site.atc },
                      { ad: 'Ödemeye başlama', v: data.huni.site.odeme },
                      { ad: 'Satın alma', v: data.huni.site.satis },
                    ]}
                    not={`Oturumdan satışa dönüşüm ${pct1(data.huni.site.satis, data.huni.site.oturum)}. Ürün görüntüleme oturumdan büyük — bir oturumda ortalama ${(data.huni.site.goruntuleme / Math.max(data.huni.site.oturum, 1)).toFixed(1).replace('.', ',')} ürün geziliyor.`}
                  />
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="mb-3 text-[13px] font-semibold text-slate-900">
                    Ana sayfadan çıkanlar <span className="font-normal text-slate-400">(previous page = ana sayfa)</span>
                  </p>
                  <Huni
                    adimlar={[
                      { ad: 'Ürün görüntüleme', v: data.huni.anaSayfa.goruntuleme },
                      { ad: 'Sepete ekleme', v: data.huni.anaSayfa.atc },
                      { ad: 'Ödemeye başlama', v: data.huni.anaSayfa.odeme },
                    ]}
                    not="Satın alma adımı yok: purchase olayı sipariş onay sayfasında ateşleniyor ve onun previous page’i ödeme sayfası, ana sayfa değil — zincir orada kopuyor, uydurma sayı koymadık."
                  />
                </div>
              </div>
            </section>
          ) : null}

          {/* 03 — ana sayfa çıkışı */}
          <section>
            <SectionHead eyebrow="03 — Ana sayfa çıkışı" title="Ana sayfadan çıkanlar nereye iniyor">
              Ana sayfada başlayıp başka bir sayfada olay üreten {nf(anaSayfa.olculenCikis)} geçiş.
              {!olcumVar ? (
                <> <b className="text-amber-700">Listeleme satırı olduğundan çok daha küçük görünüyor</b> —
                  kategori sayfaları olay göndermediği için oraya yapılan tıkların neredeyse tamamı sayılmıyor.</>
              ) : null}
            </SectionHead>
            <div className="grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
              {['urun', 'listeleme', 'arama', 'huni'].map((t) => (
                <div key={t} className="bg-white p-4">
                  <p className="text-[26px] font-bold leading-none tabular-nums text-slate-900">
                    {nf(anaSayfa.turKirilimi[t] || 0)}
                  </p>
                  <p className="mt-1 font-mono text-[12px] text-indigo-700">
                    {pct1(anaSayfa.turKirilimi[t] || 0, anaSayfa.olculenCikis)}
                  </p>
                  <p className="mt-1 text-[12.5px] text-slate-500">{TUR_ADI[t]}</p>
                  {t === 'listeleme' && !olcumVar ? (
                    <p className="mt-1 text-[11.5px] font-medium text-amber-700">yalnızca oturum başlangıcı ve sepete ekleme</p>
                  ) : null}
                </div>
              ))}
            </div>
          </section>

          {/* 04 — listeleme sayfaları */}
          <section>
            <SectionHead
              eyebrow="04 — Listeleme sayfaları"
              title={olcumVar ? 'Hangi kategori sayfası ne kadar görüntüleniyor' : 'Hangi kategori sayfası ne kadar çalışıyor'}
            >
              {olcumVar ? (
                <>Görüntülenme gerçek ölçüm. “Ana sayfadan giriş”, o sayfaya doğrudan ana sayfadan yapılan geçiş.</>
              ) : (
                <>
                  <b>Ürüne tıklama</b> = bu kategori sayfasındayken bir ürüne tıklayıp ürün sayfasını açanların
                  sayısı; sayfanın kaç kez <em>kullanıldığını</em> gösterir.{' '}
                  <b>Google’dan giriş</b> = Search Console’dan, o sayfaya Google aramasından yapılan tık —
                  gerçek sayfa trafiği, ama <em>yalnızca organik Google</em>, sitenin tamamı değil.
                </>
              )}
            </SectionHead>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 font-medium">#</th>
                      <th className="px-3 py-2 font-medium">Listeleme sayfası</th>
                      <th className="px-3 py-2 font-medium">&nbsp;</th>
                      <th className="px-3 py-2 text-right font-medium">Ürüne tıklama</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {olcumVar ? 'Görüntülenme' : "Google'dan giriş"}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {olcumVar ? 'Ana sayfadan giriş' : 'Google gösterim'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {gosterilen.map((r, i) => (
                      <tr key={r.p} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-right font-mono text-[12px] tabular-nums text-slate-400">{i + 1}</td>
                        <td className="px-3 py-2">
                          <a
                            href={`${data.meta.siteBase}${r.p}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-medium text-slate-900 hover:text-indigo-700"
                          >
                            {r.l}
                            <ExternalLink size={11} className="text-slate-300" />
                          </a>
                          <div className="font-mono text-[11px] text-slate-400">{r.p}</div>
                        </td>
                        <td className="w-[22%] px-3 py-2"><Bar value={r.urunTiki} max={maxTik} /></td>
                        <td className="px-3 py-2 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-900">
                          {nf(r.urunTiki)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[12.5px] tabular-nums">
                          {olcumVar
                            ? <span className="text-slate-700">{nf(r.gorunum)}</span>
                            : <span className="text-slate-700">{nf(r.googleTik)}</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[12.5px] tabular-nums text-slate-500">
                          {olcumVar ? nf(r.anaSayfadan) : nf(r.googleGosterim)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {liste.length > 25 ? (
                <button
                  type="button"
                  onClick={() => setHepsi((v) => !v)}
                  className="w-full border-t border-slate-100 py-2 text-[12.5px] font-medium text-indigo-700 hover:bg-slate-50"
                >
                  {hepsi ? 'İlk 25’i göster' : `Tümünü göster — ${nf(liste.length)} listeleme sayfası`}
                </button>
              ) : null}
            </div>
          </section>

          {/* 05 — kaynak kırılımı */}
          {kaynak ? (
            <section>
              <SectionHead eyebrow="05 — Bağlam" title="Ürün görüntülemesi nereden geliyor">
                Ana sayfanın sitedeki ağırlığını görmek için: {nf(kaynak.toplam)} ürün görüntülemesinin
                kullanıcının geldiği sayfaya göre dağılımı.
              </SectionHead>
              <div className="grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
                {[
                  ['dis', 'Dış siteden doğrudan ürüne', kaynak.dis],
                  ['ic', 'Site içi başka sayfadan', kaynak.ic - kaynak.anasayfa],
                  ['yok', 'Referrer yok', kaynak.yok],
                  ['arama', 'Site içi aramadan', kaynak.arama],
                  ['anasayfa', 'Ana sayfadan', kaynak.anasayfa],
                ].map(([k, ad, v]) => (
                  <div key={k} className={`p-4 ${k === 'anasayfa' ? 'bg-indigo-50' : 'bg-white'}`}>
                    <p className="text-[24px] font-bold leading-none tabular-nums text-slate-900">{nf(v)}</p>
                    <p className="mt-1 font-mono text-[12px] text-indigo-700">{pct1(v, kaynak.toplam)}</p>
                    <p className="mt-1 text-[12.5px] text-slate-500">{ad}</p>
                    {k === 'dis' && kaynak.disHost?.length ? (
                      <p className="mt-1 text-[11.5px] text-slate-400">
                        {kaynak.disHost.slice(0, 2).map(([h, n]) => `${h} ${nf(n)}`).join(' · ')}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* yöntem */}
          <section>
            <SectionHead eyebrow="Yöntem" title="Bu sayılar nereden geliyor" />
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <ul className="flex list-disc flex-col gap-2 pl-5 text-[13px] leading-relaxed text-slate-600">
                <li>
                  Kaynak GA4 Data API, property <b>{data.meta.propertyId}</b>. Dönem {dTR(data.meta.start)} – {dTR(data.meta.end)}.
                  Yanıt {data.meta.fetchMs} ms’de geldi, marka + gün için 45 dk önbelleklenir; “Tazele” önbelleği atlar.
                </li>
                <li>
                  <b>Sayfa türü veriden belirleniyor</b>, slug kalıbından değil: üzerinde <code className="rounded bg-slate-100 px-1">view_item</code>{' '}
                  ateşlenen sayfa ürün, ürüne trafik gönderen ama kendisi ürün olmayan sayfa listelemedir. Böylece her markada çalışır.
                </li>
                <li>
                  Ana sayfa referrer’ının query dizesi (gclid, utm, fbclid) atılıyor; atılmasa ana sayfa yüzlerce ayrı satıra dağılır.
                </li>
                <li>
                  <b>Oturum yolu değildir.</b> A→B→C zinciri kurulmuyor; her olay yalnızca bir önceki sayfasıyla eşleştiriliyor.
                </li>
                {!olcumVar && data.meta.olcum.teshis?.sonuc === 'yanlis-property' ? (
                  <li className="text-amber-800">
                    <b>Yanlış property bağlı:</b> site{' '}
                    <b>{(data.meta.olcum.teshis.baglanmayan || []).join(', ')}</b> kimliğine de ölçüm gönderiyor
                    ama o property Glowytics’e bağlı değil. Bağlı property ({data.meta.propertyId}) yalnızca
                    e-ticaret olaylarını alıyor. Erişim alınıp bağlandığında sayfa görüntülenmeleri geçmişiyle
                    birlikte gelir.
                  </li>
                ) : null}
                {!olcumVar && data.meta.olcum.teshis?.sonuc !== 'yanlis-property' ? (
                  <li className="text-amber-800">
                    <b>Eksik ölçüm:</b> sitede <code className="rounded bg-amber-50 px-1">page_view</code> olayı yok.
                    Listeleme sayfası görüntülenmesi ve ana sayfa → listeleme geçişi bu yüzden sayılamıyor.
                    GTM’den tek bir <code className="rounded bg-amber-50 px-1">page_view</code> etiketi eklenirse bu sayfa
                    hiçbir değişiklik gerekmeden gerçek rakamları göstermeye başlar.
                  </li>
                ) : null}
              </ul>
            </div>
          </section>

          <p className="flex items-center gap-1.5 text-[12px] text-slate-400">
            <Info size={12} /> Veri her açılışta GA4’ten canlı çekilir.
          </p>
        </div>
      ) : null}
    </div>
  );
}
